import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom"; // Importa useNavigate
import { obtenerListaClientes } from "../../../store/clientes/thunkClientes";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse"; // Componente Collapse de Material UI
import Button from "@mui/material/Button"; // Botón de Material UI
import "../../css/ColorTable.css"; // Importa el archivo CSS
import { ModalCliente } from "./ModalCliente";
import { UnificarModales } from "../Modales/UnificarModales";

export const ListaClientes = () => {
    const dispatch = useDispatch();

    const [clientes, setClientes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [clientsPerPage, setClientsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [openFilters, setOpenFilters] = useState(false); // Estado para Collapse

    // Filtros
    const [nombreCliente, setNombreCliente] = useState("");
    const [nombreEmpresa, setNombreEmpresa] = useState("");
    const [correoCliente, setCorreoCliente] = useState("");
    const [telefonoCliente, setTelefonoCliente] = useState("");
    const [tipoCliente, setTipoCliente] = useState([]);
    const [nameAdmin, setNameAdmin] = useState([]);

    // Obtener dinámicamente las opciones únicas para los selects de tipo de cliente y ejecutivos
    const tipoClienteOptions = useMemo(() => {
        const tiposUnicos = [...new Set(clientes.map((cliente) => cliente.tipo_cliente_cliente))].filter(Boolean);
        return tiposUnicos.map((tipo) => ({ value: tipo, label: tipo }));
    }, [clientes]);

    const nameAdminOptions = useMemo(() => {
        const adminsUnicos = [...new Set(clientes.map((cliente) => cliente.name_admin))].filter(Boolean);
        return adminsUnicos.map((admin) => ({ value: admin, label: admin }));
    }, [clientes]);

    useEffect(() => {
        const fetchClientes = async () => {
            if (!clientes.length) {
                // Condición para evitar llamadas innecesarias
                setLoading(true);
                const response = await dispatch(obtenerListaClientes());

                setClientes(response);
                setTotalPages(Math.ceil(response.length / clientsPerPage));
                setLoading(false);
            }
        };

        fetchClientes();
    }, [dispatch, clientsPerPage, clientes]);

    // Filtrar los clientes usando useMemo para evitar recalcular en cada render
    const filteredClientes = useMemo(() => {
        return clientes.filter((cliente) => {
            return (
                (nombreCliente === "" || cliente.nombre_cliente?.toLowerCase().includes(nombreCliente.toLowerCase())) &&
                (nombreEmpresa === "" || cliente.nombre_empresa?.toLowerCase().includes(nombreEmpresa.toLowerCase())) &&
                (correoCliente === "" || cliente.correo_cleinte?.toLowerCase().includes(correoCliente.toLowerCase())) &&
                (telefonoCliente === "" || cliente.telefono_cliente?.includes(telefonoCliente)) &&
                (tipoCliente.length === 0 || tipoCliente.some((tipo) => tipo.value === cliente.tipo_cliente_cliente)) &&
                (nameAdmin.length === 0 || nameAdmin.some((admin) => admin.value === cliente.name_admin))
            );
        });
    }, [clientes, nombreCliente, nombreEmpresa, correoCliente, telefonoCliente, tipoCliente, nameAdmin]);

    useEffect(() => {
        setTotalPages(Math.ceil(filteredClientes.length / clientsPerPage));
        setCurrentPage(1); // Reset to first page after filtering
    }, [filteredClientes, clientsPerPage]);

    // Clientes filtrados que serán paginados
    const currentClients = useMemo(() => {
        return filteredClientes.slice((currentPage - 1) * clientsPerPage, currentPage * clientsPerPage);
    }, [filteredClientes, currentPage, clientsPerPage]);

    // Funciones de cambio de página
    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Manejo de cambio de cantidad de clientes por página
    const handleClientsPerPageChange = (e) => {
        setClientsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const [selectedClient, setSelectedClient] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    // Función para manejar la navegación al hacer clic en una fila de la tabla
    const handleRowClick = (clienteId) => {
        setSelectedClient(clienteId);
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedClient(null);
    };

    return (
        <>
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                    <CircularProgress /> {/* Círculo de carga mientras se cargan los clientes */}
                </div>
            ) : (
                <div className="mb-3">
                    {/* Botón para abrir/cerrar filtros */}
                    <Button onClick={() => setOpenFilters(!openFilters)} aria-expanded={openFilters} aria-label="show filters" variant="contained" style={{ marginBottom: "15px" }}>
                        {openFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
                    </Button>

                    {/* Collapse para los filtros */}
                    <Collapse in={openFilters}>
                        <div className="row">
                            <div className="col-md-4 mb-2">
                                <input type="text" className="form-control" placeholder="Buscar por Nombre" value={nombreCliente} onChange={(e) => setNombreCliente(e.target.value)} />
                            </div>
                            <div className="col-md-4 mb-2">
                                <input type="text" className="form-control" placeholder="Buscar por Empresa" value={nombreEmpresa} onChange={(e) => setNombreEmpresa(e.target.value)} />
                            </div>
                            <div className="col-md-4 mb-2">
                                <input type="text" className="form-control" placeholder="Buscar por Correo" value={correoCliente} onChange={(e) => setCorreoCliente(e.target.value)} />
                            </div>
                            <div className="col-md-4 mb-2">
                                <input type="text" className="form-control" placeholder="Buscar por Teléfono" value={telefonoCliente} onChange={(e) => setTelefonoCliente(e.target.value)} />
                            </div>
                            <div className="col-md-4 mb-2">
                                <Select isMulti options={tipoClienteOptions} value={tipoCliente} onChange={setTipoCliente} placeholder="Tipo de Cliente" />
                            </div>
                            <div className="col-md-4 mb-2">
                                <Select isMulti options={nameAdminOptions} value={nameAdmin} onChange={setNameAdmin} placeholder="Ejecutivo" />
                            </div>
                        </div>
                    </Collapse>

                    <div className="d-flex justify-content-between mb-3">
                        <div className="d-flex justify-content-between mb-3">
                            <select id="clientsPerPage" className="form-select" value={clientsPerPage} onChange={handleClientsPerPageChange} style={{ width: "100px" }}>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={30}>30</option>
                                <option value={50}>50</option>
                                <option value={70}>70</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                    <UnificarModales />
                    <br />
                    {/* Tabla */}
                    <div className="table-responsive">
                        <table className="table table-striped table dt-responsive w-100 display text-left hoverable-table" style={{ fontSize: "15px", width: "100%", textAlign: "left" }}>
                            <thead>
                                <tr>
                                    <th>Nombre Cliente</th>
                                    <th>Empresa</th>
                                    <th>Correo</th>
                                    <th>Teléfono</th>
                                    <th>Tipo Cliente</th>
                                    <th>Crédito</th>
                                    <th>Ejecutivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentClients.map((cliente) => (
                                    <tr key={cliente.id_cliente} onClick={() => handleRowClick(cliente)} style={{ cursor: "pointer" }}>
                                        <td>{cliente.nombre_cliente || "SIN NOMBRE"}</td>
                                        <td>{cliente.nombre_empresa || "SIN EMPRESA"}</td>
                                        <td>{cliente.correo_cleinte || "SIN CORREO"}</td>
                                        <td>{cliente.telefono_cliente || "SIN TELÉFONO"}</td>
                                        <td>{cliente.tipo_cliente_cliente || "SIN TIPO"}</td>
                                        <td>{cliente.credito_cliente || "SIN MONTO DE CREDITO"}</td>
                                        <td>{cliente.name_admin || "SIN ASIGNAR"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    <div className="pagination-controls" style={{ textAlign: "center", marginTop: "20px" }}>
                        <button onClick={handlePrevPage} disabled={currentPage === 1} className="btn btn-light">
                            Anterior
                        </button>
                        <span style={{ margin: "0 10px" }}>
                            Página {currentPage} de {totalPages}
                        </span>
                        <button onClick={handleNextPage} disabled={currentPage === totalPages} className="btn btn-light">
                            Siguiente
                        </button>
                    </div>
                </div>
            )}
            {openModal && selectedClient && <ModalCliente client={selectedClient} onClose={handleCloseModal} />}
        </>
    );
};
